import type {Feature} from "../feature";
import {classifyFeature} from "./classifyFeature";

/**
 * Bidirectional ring fusion record.
 * Maps a feature index (in the input array) to its fused-to feature indices
 * along with the atom indices shared with each.
 */
export interface FusionMap {
    /** featureIndex → list of {otherFeatureIndex, sharedAtomIndices} */
    [featureIndex: number]: Array<{
        otherFeatureIndex: number;
        sharedAtomIndices: number[];
    }>;
}

/**
 * Compute pairwise ring fusion relationships.
 *
 * Two ring-like features are "fused" when they share exactly 1 or 2 atoms.
 * Sharing >2 atoms within a single ring system is bicyclic/bridged
 * territory (already merged into a single `bridged_bicyclic` feature by
 * `bridgeheadFilter`, so won't appear here as two features). However,
 * a `bridged_bicyclic` primary can still be fused to a *separate* ring
 * (PIPH-251: norbornane fused to benzene at a 2-atom edge, or spiro at
 * 1 atom), so both `ring` and `bridged_bicyclic` features participate in
 * the pair-walk.
 *
 * Result is bidirectional: if A is fused to B, B is also fused to A.
 */
export function computeFusion(features: Feature[]): FusionMap {
    const map: FusionMap = {};

    // Consider ring-typed and bridged-bicyclic primaries (PIPH-251).
    const ringIndices: number[] = [];
    for (let i = 0; i < features.length; i++) {
        const t = classifyFeature(features[i]);
        if (t === "ring" || t === "bridged_bicyclic") {
            ringIndices.push(i);
        }
    }

    for (let a = 0; a < ringIndices.length; a++) {
        for (let b = a + 1; b < ringIndices.length; b++) {
            const i = ringIndices[a];
            const j = ringIndices[b];
            const ringA = features[i];
            const ringB = features[j];

            const setB = new Set(ringB.atoms);
            const shared = ringA.atoms.filter(atom => setB.has(atom));

            if (shared.length >= 1 && shared.length <= 2) {
                const sharedIndices = shared.map(a => a.index);

                if (!map[i]) map[i] = [];
                if (!map[j]) map[j] = [];
                map[i].push({otherFeatureIndex: j, sharedAtomIndices: sharedIndices});
                map[j].push({otherFeatureIndex: i, sharedAtomIndices: sharedIndices});
            }
        }
    }

    return map;
}
